import { LightningElement } from 'lwc';

import { fetchAuthSession } from 'aws-amplify/auth';
import { get, post } from 'aws-amplify/api';

import { IS_TESTING_NO_INTERNET } from '../../../utils/basicUtils';

const TAB_DEFS = [
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'past', label: 'Past' },
    { key: 'canceled', label: 'Canceled' }
];

const STATUS_LABELS = {
    confirmed: 'Confirmed',
    completed: 'Completed',
    pending_reschedule: 'Pending reschedule',
    canceled: 'Canceled',
    cancelled: 'Canceled'
};

function compactJoin(values, separator = ', ') {
    return values.filter(Boolean).join(separator);
}

function normalizeStatus(value) {
    const status = String(value || '').trim().toLowerCase();
    return status || 'confirmed';
}

function isCanceledStatus(value) {
    return ['canceled', 'cancelled'].includes(normalizeStatus(value));
}

function parseDateTime(value) {
    if (!value) return null;

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
    const date = parseDateTime(value);

    if (!date) return 'Date not provided';

    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    }).format(date);
}

function formatTime(value) {
    const date = parseDateTime(value);

    if (!date) return '';

    return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit'
    }).format(date);
}

function formatDateRange(startTime, endTime) {
    const startLabel = formatDateTime(startTime);
    const endLabel = formatTime(endTime);

    return endLabel ? `${startLabel} - ${endLabel}` : startLabel;
}

function toDateInputValue(value) {
    const date = parseDateTime(value);

    if (!date) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function toTimeInputValue(value) {
    const date = parseDateTime(value);

    if (!date) return '';

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
}

function combineLocalDateTime(dateValue, timeValue) {
    if (!dateValue || !timeValue) return null;

    const date = new Date(`${dateValue}T${timeValue}`);

    return Number.isNaN(date.getTime()) ? null : date;
}

function formatCurrencyFromCents(cents, currency = 'USD') {
    const amount = Number(cents);

    if (!Number.isFinite(amount)) {
        return '$0.00';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: String(currency || 'USD').toUpperCase()
    }).format(amount / 100);
}

function createSampleReservations() {
    const now = new Date();
    const inThreeDays = new Date(now);
    inThreeDays.setDate(now.getDate() + 3);
    inThreeDays.setHours(14, 0, 0, 0);

    const inTwoWeeks = new Date(now);
    inTwoWeeks.setDate(now.getDate() + 14);
    inTwoWeeks.setHours(11, 30, 0, 0);

    const lastMonth = new Date(now);
    lastMonth.setMonth(now.getMonth() - 1);
    lastMonth.setHours(13, 0, 0, 0);

    const canceled = new Date(now);
    canceled.setDate(now.getDate() - 8);
    canceled.setHours(10, 0, 0, 0);

    return [
        {
            id: 'sample-upcoming-1',
            listing_id: '1',
            property_name: 'Palm Courtyard Pool',
            start_time: inThreeDays.toISOString(),
            end_time: new Date(inThreeDays.getTime() + 2 * 60 * 60 * 1000).toISOString(),
            street: '742 Waterline Ave',
            city: 'San Diego',
            state: 'CA',
            guest_count: 4,
            status: 'confirmed',
            total_price_cents: 12600
        },
        {
            id: 'sample-upcoming-2',
            listing_id: '2',
            property_name: 'Hilltop Swim Spot',
            start_time: inTwoWeeks.toISOString(),
            end_time: new Date(inTwoWeeks.getTime() + 3 * 60 * 60 * 1000).toISOString(),
            city: 'Austin',
            state: 'TX',
            guest_count: 6,
            status: 'confirmed',
            total_price_cents: 19800
        },
        {
            id: 'sample-past-1',
            listing_id: '3',
            property_name: 'Citrus Lane Pool',
            start_time: lastMonth.toISOString(),
            end_time: new Date(lastMonth.getTime() + 2 * 60 * 60 * 1000).toISOString(),
            formatted_address: '18 Citrus Lane, Phoenix, AZ',
            city: 'Phoenix',
            state: 'AZ',
            guest_count: 3,
            status: 'confirmed',
            display_status: 'completed',
            total_price_cents: 8400
        },
        {
            id: 'sample-canceled-1',
            listing_id: '4',
            property_name: 'Backyard Baja Pool',
            start_time: canceled.toISOString(),
            end_time: new Date(canceled.getTime() + 2 * 60 * 60 * 1000).toISOString(),
            city: 'Durango',
            state: 'DGO',
            country: 'MX',
            guest_count: 5,
            status: 'canceled',
            total_price_cents: 0
        }
    ];
}

export default class GwGuestActivity extends LightningElement {
    _isLoading = false;
    _idToken;
    activeTab = 'upcoming';
    reservations = [];
    toastObj;
    actionReservationId = null;
    rescheduleForm = null;

    async connectedCallback() {
        await this.loadReservations();
    }

    get tabs() {
        return TAB_DEFS.map(tab => {
            const count = this[`${tab.key}Reservations`]?.length || 0;
            const isActive = this.activeTab === tab.key;

            return {
                ...tab,
                count,
                className: isActive
                    ? 'activity-tab activity-tab_active'
                    : 'activity-tab',
                ariaSelected: isActive ? 'true' : 'false',
                tabIndex: isActive ? '0' : '-1'
            };
        });
    }

    get upcomingReservations() {
        return this.reservations
            .filter(reservation => reservation.groupKey === 'upcoming')
            .sort((left, right) => left.startMs - right.startMs);
    }

    get pastReservations() {
        return this.reservations
            .filter(reservation => reservation.groupKey === 'past')
            .sort((left, right) => right.startMs - left.startMs);
    }

    get canceledReservations() {
        return this.reservations
            .filter(reservation => reservation.groupKey === 'canceled')
            .sort((left, right) => right.startMs - left.startMs);
    }

    get activeReservations() {
        if (this.activeTab === 'past') {
            return this.pastReservations;
        }

        if (this.activeTab === 'canceled') {
            return this.canceledReservations;
        }

        return this.upcomingReservations;
    }

    get hasActiveReservations() {
        return this.activeReservations.length > 0;
    }

    get isRescheduleOpen() {
        return Boolean(this.rescheduleForm);
    }

    get selectedRescheduleReservation() {
        if (!this.actionReservationId) return null;

        return this.reservations.find(item => item.id === this.actionReservationId) || null;
    }

    get minRescheduleDate() {
        return toDateInputValue(new Date());
    }

    get activeEmptyTitle() {
        if (this.activeTab === 'past') {
            return 'No past reservations';
        }

        if (this.activeTab === 'canceled') {
            return 'No canceled reservations';
        }

        return 'No upcoming reservations';
    }

    get activeEmptyHelpText() {
        if (this.activeTab === 'past') {
            return 'Completed pool reservations will appear here.';
        }

        if (this.activeTab === 'canceled') {
            return 'Canceled reservations will appear here.';
        }

        return 'Your next confirmed pool reservation will appear here.';
    }

    async getIdToken() {
        if (!this._idToken) {
            const session = await fetchAuthSession();
            this._idToken = session.tokens?.idToken?.toString();
        }

        return this._idToken;
    }

    async loadReservations() {
        this._isLoading = true;

        try {
            if (IS_TESTING_NO_INTERNET) {
                this.reservations = this.formatReservations(createSampleReservations());
                return;
            }

            const idToken = await this.getIdToken();
            if (!idToken) {
                throw new Error('No idToken (not signed in)');
            }

            const op = get({
                apiName: 'api',
                path: '/cognito/reservations',
                options: {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                },
            });

            const { body } = await op.response;
            const payload = await body.json();

            this.reservations = this.formatReservations(payload?.reservations || []);
        } catch (error) {
            this.showToast('Error loading activity', error?.message || error, 'error');
        } finally {
            this._isLoading = false;
        }
    }

    formatReservations(reservations = []) {
        const nowMs = Date.now();

        return reservations.map(reservation => {
            const startTime = reservation.start_time || reservation.startTime;
            const endTime = reservation.end_time || reservation.endTime;
            const startDate = parseDateTime(startTime);
            const endDate = parseDateTime(endTime);
            const startMs = startDate?.getTime() || 0;
            const endMs = endDate?.getTime() || startMs;
            const rawStatus = normalizeStatus(reservation.status);
            const displayStatus = normalizeStatus(reservation.display_status || reservation.displayStatus || rawStatus);
            const statusKey = isCanceledStatus(rawStatus) || isCanceledStatus(displayStatus)
                ? 'canceled'
                : displayStatus === 'pending_reschedule'
                    ? 'pending_reschedule'
                    : displayStatus === 'completed' || endMs < nowMs
                    ? 'completed'
                    : 'confirmed';
            const cityLine = compactJoin([
                reservation.city,
                reservation.state,
                reservation.zip_code || reservation.zipCode
            ]);
            const addressLabel = compactJoin([
                reservation.street || reservation.formatted_address || reservation.formattedAddress,
                cityLine || reservation.country
            ]) || 'Address not available';
            const groupKey = statusKey === 'canceled'
                ? 'canceled'
                : statusKey === 'completed'
                    ? 'past'
                    : 'upcoming';

            return {
                id: reservation.id || reservation.reservation_id || reservation.reservationId,
                listingId: reservation.listing_id || reservation.listingId,
                startTime,
                endTime,
                paymentStatus: reservation.payment_status || reservation.paymentStatus,
                refundedCents: reservation.refunded_cents || reservation.refundedCents || 0,
                poolName: reservation.property_name || reservation.pool_name || reservation.poolName || 'GetWet pool',
                dateTimeLabel: formatDateRange(startTime, endTime),
                addressLabel,
                locationLabel: compactJoin([reservation.city, reservation.state, reservation.country]),
                guestCountLabel: this.formatGuestCount(reservation.guest_count || reservation.guestCount),
                statusLabel: STATUS_LABELS[statusKey] || STATUS_LABELS.confirmed,
                statusClass: `activity-status activity-status_${statusKey}`,
                totalPaidLabel: formatCurrencyFromCents(
                    reservation.total_price_cents || reservation.totalPaidCents || reservation.total_paid_cents,
                    reservation.currency || 'USD'
                ),
                startMs,
                groupKey,
                receiptUrl: reservation.receipt_url || reservation.receiptUrl || null,
                actions: this.getActionsForGroup(groupKey)
            };
        });
    }

    formatGuestCount(value) {
        const count = Number(value);

        if (!Number.isFinite(count) || count <= 0) {
            return 'Not provided';
        }

        return `${count} guest${count === 1 ? '' : 's'}`;
    }

    getActionsForGroup(groupKey) {
        if (groupKey === 'upcoming') {
            return [
                { label: 'Cancel', value: 'cancel', className: 'slds-button slds-button_neutral' },
                { label: 'Reschedule', value: 'reschedule', className: 'slds-button slds-button_neutral' },
                { label: 'Contact host', value: 'contactHost', className: 'slds-button slds-button_brand' }
            ];
        }

        if (groupKey === 'past') {
            return [
                { label: 'Receipt', value: 'receipt', className: 'slds-button slds-button_neutral' },
                { label: 'Details', value: 'details', className: 'slds-button slds-button_neutral' },
                { label: 'Rebook', value: 'rebook', className: 'slds-button slds-button_brand' },
                { label: 'Review', value: 'review', className: 'slds-button slds-button_neutral' }
            ];
        }

        return [
            { label: 'Details', value: 'details', className: 'slds-button slds-button_neutral' },
            { label: 'Rebook', value: 'rebook', className: 'slds-button slds-button_brand' }
        ];
    }

    handleTabClick(event) {
        const tab = event.currentTarget?.dataset?.tab;

        if (tab) {
            this.activeTab = tab;
        }
    }

    isWithinChangeWindow(reservation) {
        const startMs = Number(reservation?.startMs);

        return Number.isFinite(startMs) && startMs - Date.now() < 24 * 60 * 60 * 1000;
    }

    warnContactHostOnly() {
        this.showToast(
            'Contact the host',
            'This booking is less than 24 hours away. Please contact the host to cancel or reschedule.',
            'warning',
            'sticky'
        );
    }

    async handleReservationAction(event) {
        const action = event.currentTarget?.dataset?.action;
        const reservationId = event.currentTarget?.dataset?.reservationId;
        const reservation = this.reservations.find(item => item.id === reservationId);

        if (!action || !reservation) return;

        if (action === 'contactHost') {
            this.dispatchEvent(new CustomEvent('contacthost', {
                detail: { listingId: reservation.listingId },
                bubbles: true,
                composed: true
            }));
            return;
        }

        if (['details', 'rebook'].includes(action)) {
            this.dispatchEvent(new CustomEvent('viewlisting', {
                detail: { listingId: reservation.listingId },
                bubbles: true,
                composed: true
            }));
            return;
        }

        if (action === 'receipt' && reservation.receiptUrl) {
            window.open(reservation.receiptUrl, '_blank', 'noopener');
            return;
        }

        if (action === 'cancel') {
            if (this.isWithinChangeWindow(reservation)) {
                this.warnContactHostOnly();
                return;
            }

            await this.cancelReservation(reservation);
            return;
        }

        if (action === 'reschedule') {
            if (this.isWithinChangeWindow(reservation)) {
                this.warnContactHostOnly();
                return;
            }

            this.openRescheduleForm(reservation);
            return;
        }

        const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
        this.showToast(`${actionLabel} coming soon`, 'This reservation action is not available yet.', 'info', 'dismissible');
    }

    async apiPost(path, body) {
        const idToken = await this.getIdToken();
        if (!idToken) {
            throw new Error('No idToken (not signed in)');
        }

        const op = post({
            apiName: 'api',
            path,
            options: {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                },
                body
            }
        });

        const { body: responseBody } = await op.response;
        return responseBody.json();
    }

    async cancelReservation(reservation) {
        const confirmed = window.confirm(
            `Cancel your reservation for ${reservation.poolName}? Your payment will be refunded if it is eligible.`
        );

        if (!confirmed) return;

        this._isLoading = true;

        try {
            await this.apiPost('/cognito/reservations/cancel', {
                reservationId: reservation.id
            });
            this.showToast('Reservation canceled', 'The host was notified and the refund was submitted.', 'success');
            await this.loadReservations();
            this.activeTab = 'canceled';
        } catch (error) {
            await this.handleReservationActionError(error);
        } finally {
            this._isLoading = false;
        }
    }

    openRescheduleForm(reservation) {
        this.actionReservationId = reservation.id;
        this.rescheduleForm = {
            date: toDateInputValue(reservation.startTime),
            startTime: toTimeInputValue(reservation.startTime),
            endTime: toTimeInputValue(reservation.endTime)
        };
    }

    closeRescheduleForm() {
        this.actionReservationId = null;
        this.rescheduleForm = null;
    }

    handleRescheduleInput(event) {
        const field = event.currentTarget?.dataset?.field;

        if (!field || !this.rescheduleForm) return;

        this.rescheduleForm = {
            ...this.rescheduleForm,
            [field]: event.currentTarget.value
        };
    }

    async submitReschedule() {
        if (!this.rescheduleForm || !this.actionReservationId) return;

        const startDate = combineLocalDateTime(
            this.rescheduleForm.date,
            this.rescheduleForm.startTime
        );
        const endDate = combineLocalDateTime(
            this.rescheduleForm.date,
            this.rescheduleForm.endTime
        );

        if (!startDate || !endDate || endDate <= startDate) {
            this.showToast('Check the time', 'Choose a valid date, start time, and end time.', 'error');
            return;
        }

        this._isLoading = true;

        try {
            await this.apiPost('/cognito/reservations/reschedule', {
                reservationId: this.actionReservationId,
                startTime: startDate.toISOString(),
                endTime: endDate.toISOString()
            });
            this.closeRescheduleForm();
            this.showToast('Reschedule requested', 'The host was notified to approve the new time.', 'success');
            await this.loadReservations();
            this.activeTab = 'upcoming';
        } catch (error) {
            await this.handleReservationActionError(error);
        } finally {
            this._isLoading = false;
        }
    }

    async handleReservationActionError(error) {
        const errorBody = await this.getApiErrorBody(error);

        if (errorBody?.contactHostOnly || errorBody?.code === 'WITHIN_24_HOURS') {
            this.warnContactHostOnly();
            return;
        }

        this.showToast(
            'Reservation action failed',
            errorBody?.error || error?.message || 'Unable to update this reservation.',
            'error'
        );
    }

    async getApiErrorBody(error) {
        const responseBody = error?.response?.body;

        if (!responseBody) return null;

        if (typeof responseBody.json === 'function') {
            try {
                return await responseBody.json();
            } catch (jsonError) {
                return null;
            }
        }

        return responseBody;
    }

    showToast(title, message, variant, mode=null) {
        const modeP = mode
            ? mode
            : variant === 'success'
                ? 'dismissible'
                : 'sticky';

        this.toastObj = {
            variant,
            mode: modeP,
            message,
            header: {
                text: title
            }
        };
    }
}
